import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { capitalize } from "lodash";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import {
  PeopleFillIcon,
  PersonFilledIcon,
  PlaceIcon,
  LessonsIconV2,
  RecurringIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Text,
  Box,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { RoundListAdditionalLessonInfoItemDisplayQuery } from "./__generated__/RoundListAdditionalLessonInfoItemDisplayQuery.graphql";
import { RoundListAdditionalLessonInfoItemDisplay_Booking$key } from "./__generated__/RoundListAdditionalLessonInfoItemDisplay_Booking.graphql";
import { AdditionalLessonInfoItemType } from "./types";

type Props = {
  bookingFragmentRef: RoundListAdditionalLessonInfoItemDisplay_Booking$key | null;
  item: AdditionalLessonInfoItemType;
  lessonPriceQueryRef: PreloadedQuery<RoundListAdditionalLessonInfoItemDisplayQuery>;
};

export const RoundListAdditionalLessonInfoItemDisplay = ({
  bookingFragmentRef,
  item,
  lessonPriceQueryRef,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = useFragment(
    RoundListAdditionalLessonInfoItemDisplayFragment,
    bookingFragmentRef
  );
  const lessonPriceData = usePreloadedQuery(
    RoundListAdditionalLessonInfoItemDisplayQueryString,
    lessonPriceQueryRef
  );

  const lessonPrice =
    lessonPriceData?.node?.bookingRounds?.edges?.reduce(
      (sum, edge) =>
        sum +
        (edge?.node?.roundRate?.rate?.__typename === "LessonRate"
          ? edge.node.roundRate.rate.totalDurationPrice ?? 0
          : 0),
      0
    ) ?? 0;

  const shouldShowLessonPrices =
    !(booking?.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false) &&
    (booking?.startSlot?.agenda?.facility?.privateLessonsPermissionSet
      ?.showPrices ??
      false);

  const isRequest = booking?.startSlot?.__typename === "QueueSlot";

  const courseNoun = getNounOverride(
    booking?.startSlot?.agenda?.facility?.copyOverrides?.course?.singular,
    t`Venue`
  );
  const venueLabel = capitalize(courseNoun);
  const capacity =
    lessonPriceData?.node?.startSlot?.__typename === "QueueSlot" ||
    lessonPriceData?.node?.startSlot?.__typename === "TimeSlot"
      ? lessonPriceData.node.startSlot.lessonCapacity
      : 0;
  const shouldShowRecurringLesson =
    !!booking?.seriesIndex && (booking?.seriesCount ?? 0) > 1;

  const instructor = booking?.instructors?.edges?.length
    ? booking?.instructors?.edges[0]?.node
    : null;

  if (
    item.type === "BOOKING_FEE" ||
    (item.type === "VENUE" && isRequest) ||
    (item.type === "RECURRING" && !shouldShowRecurringLesson)
  ) {
    return null;
  }

  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      paddingVertical={2}
    >
      {/* left side */}
      <HStack gap={3} alignItems="center">
        {item.type !== null ? (
          <Box
            width={24}
            height={24}
            borderRadius="2"
            backgroundColor="surface-main-primary-subtlest"
            justifyContent="center"
            alignItems="center"
          >
            {item.type === "INSTRUCTOR" ? <PersonFilledIcon /> : null}
            {item.type === "VENUE" ? <PlaceIcon /> : null}
            {item.type === "LESSON_PRICE" ? <LessonsIconV2 /> : null}
            {item.type === "RECURRING" ? <RecurringIcon /> : null}
            {item.type === "CAPACITY" ? <PeopleFillIcon /> : null}
          </Box>
        ) : null}
        <Text variant="mainBaseSemiBold" color="text-primary">
          {item.type === "INSTRUCTOR" ? t`Instructor` : null}
          {item.type === "VENUE" ? venueLabel : null}
          {item.type === "LESSON_PRICE"
            ? t`${booking?.lessonType?.name ?? ""} (${
                booking?.durationMins ?? 0
              } min)`
            : null}
          {item.type === "RECURRING" ? t`Recurring Lesson` : null}
          {item.type === "CAPACITY" ? t`Allowed Capacity` : null}
        </Text>
      </HStack>

      {/* right side */}
      <Text variant="mainBaseSemiBold" color="text-primary">
        {item.type === "INSTRUCTOR" && instructor?.user?.fullName
          ? instructor?.user?.fullName
          : null}
        {item.type === "VENUE" ? booking?.startSlot?.course?.name : null}
        {item.type === "LESSON_PRICE" && shouldShowLessonPrices
          ? `${currencySymbol}${ConvertCentsToDollars(lessonPrice)}`
          : null}
        {item.type === "RECURRING"
          ? t`${booking?.seriesIndex ?? 0} of ${booking?.seriesCount ?? 0}`
          : null}
        {item.type === "CAPACITY"
          ? capacity
            ? capacity === 1
              ? t`1 Person`
              : t`1-${capacity} People`
            : "-"
          : null}
      </Text>
    </HStack>
  );
};

export const RoundListAdditionalLessonInfoItemDisplayQueryString = graphql`
  query RoundListAdditionalLessonInfoItemDisplayQuery(
    $bookingId: ID!
    $duration: Int
    $groupSize: Int
    $lessonTypeId: ID!
  ) {
    node(id: $bookingId) {
      ... on LessonBooking {
        startSlot {
          __typename
          ... on QueueSlot {
            lessonCapacity: capacity(
              bookingType: LESSON
              lessonTypeId: $lessonTypeId
            )
          }
          ... on TimeSlot {
            lessonCapacity: capacity(
              bookingType: LESSON
              lessonTypeId: $lessonTypeId
            )
          }
        }
        bookingRounds: rounds {
          edges {
            node {
              roundRate {
                rate {
                  __typename
                  ... on LessonRate {
                    totalDurationPrice(
                      duration: $duration
                      playerCount: $groupSize
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const RoundListAdditionalLessonInfoItemDisplayFragment = graphql`
  fragment RoundListAdditionalLessonInfoItemDisplay_Booking on Booking {
    __typename
    startSlot {
      __typename
      ... on TimeSlot {
        course {
          name
        }
      }
      agenda {
        facility {
          copyOverrides {
            course {
              singular
            }
          }
          club {
            supportsPrivate
          }
          privateLessonsPermissionSet {
            showPrices
          }
        }
      }
    }
    ... on StandardBooking {
      seriesIndex
      seriesCount
    }
    ... on LessonBooking {
      seriesIndex
      seriesCount
      lessonType {
        name
      }
      durationMins
      instructors {
        edges {
          node {
            user {
              fullName
            }
          }
        }
      }
    }
  }
`;
